import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { TablePagination } from "@mui/material";
import { Download, Search, Edit2, Calendar, Clock, FileText, X, RefreshCw, Plus } from "lucide-react";
import { fetchExternalList } from "./helper";
import InvoiceForm from "../../../components/InvoiceForm";

const ExternalPaymemnt = () => {
    const context = useOutletContext();
    const [status] = useState("tax");
    const [usersPage, setUsersPage] = useState(0);
    const [usersLimit, setUsersLimit] = useState(10);
    const [usersTotal, setUsersTotal] = useState(0);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedInvoiceForEdit, setSelectedInvoiceForEdit] = useState(null);
    const [duration, setDuration] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [selectedInvoiceForTaxConvert, setSelectedInvoiceForTaxConvert] = useState(null);
    const [isConverting, setIsConverting] = useState(false);
    const [totalAmount, setTotalAmount] = useState(0);
    const [showCreateInvoiceForm, setShowCreateInvoiceForm] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchTerm]);

    const loadInvoices = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchExternalList(
                usersPage,
                usersLimit,
                status,
                debouncedSearchTerm,
                duration,
                startDate,
                endDate
            );

            if (response && response.data) {
                if (response.data.success && response.data.data) {
                    setInvoices(response.data.data.result || []);
                    setUsersTotal(response.data.data.meta?.total || 0);
                    setTotalAmount(response.data.data?.totalAmount || 0);
                } else {
                    setInvoices(response.data.content || response.data.invoices || response.data);
                    setUsersTotal(response.data.totalElements || response.data.total || 0);
                    setTotalAmount(response.data.data?.totalAmount || 0);
                }
            }
        } catch (err) {
            console.error("Error fetching invoices:", err);
            setError("Failed to load invoices");
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        context(["Payments & Invoice ", "External Payments"]);
    }, []);

    useEffect(() => {
        loadInvoices();
    }, [usersPage, usersLimit, debouncedSearchTerm, duration, startDate, endDate]);

    useEffect(() => {
        if (usersPage !== 0) {
            setUsersPage(0);
        }
    }, [debouncedSearchTerm, duration, startDate, endDate]);

    const handleChangePage = (event, newPage) => {
        setUsersPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setUsersLimit(parseInt(event.target.value, 10));
        setUsersPage(0);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
    };

    const handleDurationChange = (event) => {
        const newDuration = event.target.value;
        setDuration(newDuration);
        if (newDuration === 'Monthly') {
            setStartDate('');
            setEndDate('');
        }
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
        if (endDate && event.target.value > endDate) {
            setEndDate('');
        }
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const clearDateFilters = () => {
        setStartDate('');
        setEndDate('');
    };

    const clearAllFilters = () => {
        setSearchTerm('');
        setDuration('');
        setStartDate('');
        setEndDate('');
    };

    const handleCreateInvoice = () => {
        setSelectedInvoiceForEdit(null);
        setShowCreateInvoiceForm(true);
    };

    const handleCloseCreateInvoiceForm = () => {
        setShowCreateInvoiceForm(false);
        loadInvoices();
    };

    const handleDownload = async (downloadUrl, invoiceId) => {
        if (!downloadUrl) {
            alert("Download URL not available");
            return;
        }

        try {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `Invoice_${invoiceId}.pdf`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading invoice:", error);
            alert("Failed to download invoice");
        }
    };

    const handleEditInvoice = (invoice) => {
        console.log('Editing invoice:', invoice);
        setSelectedInvoiceForEdit(invoice);
        setIsEditModalOpen(true);
    };

    const handleCloseEditModal = () => {
        setIsEditModalOpen(false);
        setSelectedInvoiceForEdit(null);
        loadInvoices();
    };

    const handleCreateTaxInvoice = (invoice) => {
        setSelectedInvoiceForTaxConvert(invoice);
        setShowConfirmModal(true);
    };

    const handleConfirmTaxConversion = async () => {
        if (!selectedInvoiceForTaxConvert) return;

        setIsConverting(true);
        try {
            const taxInvoiceData = {
                ...selectedInvoiceForTaxConvert,
                invoiceType: 'taxbased'
            };
            setSelectedInvoiceForEdit(taxInvoiceData);
            setIsEditModalOpen(true);
            setShowConfirmModal(false);
            setSelectedInvoiceForTaxConvert(null);
        } catch (error) {
            console.error("Error converting to tax invoice:", error);
            alert("Failed to convert to tax invoice");
        } finally {
            setIsConverting(false);
        }
    };

    const handleCancelTaxConversion = () => {
        setShowConfirmModal(false);
        setSelectedInvoiceForTaxConvert(null);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            return new Date(dateString).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: '2-digit'
            });
        } catch (error) {
            return dateString;
        }
    };

    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };
    const shouldShowDateFilters = duration !== 'Monthly';

    return (
        <>
            <div className="p-5 shadow-md bg-white">
                <div className="flex flex-row justify-between items-center gap-1 mb-4">
                    <h3 className="text-lg w-[25%] flex flex-col uppercase tracking-wider font-bold" style={{ color: "#2EBAA3" }}>
                    <span>External Payments</span>
                    <span className="text-gray-600 text-sm">(Total Amount: ₹{totalAmount || "0.00"})</span> 
                    </h3>

                    <div className="flex items-center gap-2">
                        {/* <button
                            onClick={handleCreateInvoice} 
                            className="bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-4 py-2 rounded-md shadow flex items-center"
                        >
                            Create Invoice
                        </button> */}

                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search className="h-4 w-4 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by company name..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm w-44"
                            />
                            {searchTerm && (
                                <button
                                    onClick={clearSearch}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Clock className="h-4 w-4 text-gray-400" />
                            </div>
                            <select
                                value={duration}
                                onChange={handleDurationChange}
                                className="pl-7 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm w-32 bg-white"
                            >
                                <option value="">All Durations</option>
                                <option value="Monthly">Monthly</option>
                                <option value="Yearly">Yearly</option>
                            </select>
                        </div>
                {shouldShowDateFilters && (
                    <>
                        <div className="relative">
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                                max={getTodayDate()}
                                placeholder="Start Date"
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm w-32"
                            />
                        </div>
                        <div className="relative">
                            <input
                                type="date"
                                value={endDate}
                                onChange={handleEndDateChange}
                                min={startDate || undefined}
                                max={getTodayDate()}
                                placeholder="End Date"
                                className="px-2 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm w-32"
                            />
                        </div>
                    </>
                )}

                        <button
                            onClick={clearAllFilters}
                            className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                            Clear All
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto relative w-full">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
                            <tr>
                                <th className="py-3 px-6">S. No.</th>
                                <th className="py-3 px-6">Invoice No.</th>
                                <th className="py-3 px-6">Company Name</th>
                                <th className="py-3 px-6">Company Email</th>
                                <th className="py-3 px-6">Created On</th>
                                <th className="py-3 px-6">Amount</th>
                                <th className="py-3 px-6">GST</th>
                                <th className="py-3 px-6">Total Amount</th>
                                <th className="py-3 px-6">Content</th>
                                <th className="py-3 px-6">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="10" className="py-4 px-6 text-center">
                                        Loading invoices...
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan="10" className="py-4 px-6 text-center">
                                        {debouncedSearchTerm || duration || startDate || endDate ?
                                            "No invoices found matching the selected filters" :
                                            "No invoices found"
                                        }
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((invoice, index) => (
                                    <tr className="bg-white border-b" key={invoice.id || index}>
                                        <td className="py-4 px-6">{usersPage * usersLimit + index + 1}</td>
                                        <td className="py-4 px-6">{invoice.invoiceNumber || invoice.id || 'N/A'}</td>
                                        <td className="py-4 px-6">{invoice.companyName || 'N/A'}</td>
                                        <td className="py-4 px-6">{invoice.companyEmail || 'N/A'}</td>
                                        <td className="py-4 px-6">{formatDate(invoice.created_at)}</td>
                                        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.amount || 0}</td>
                                        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.gst || 0}</td>
                                        <td className="py-4 px-6">{invoice?.currency === 'USD' ? '$' : '₹'}{invoice.total || 0}</td>
                                        <td className="py-4 px-6">{invoice.description || 'N/A'}</td>
                                        <td className="py-4 px-">
                                            <div className="flex flex-col gap-1 items-start justify-center">
                                                <div className="flex flex-row gap-2">
                                                    {status === 'standard' && (
                                                        <button
                                                            onClick={() => handleEditInvoice(invoice)}
                                                            className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                                            title="Edit Invoice"
                                                        >
                                                            <Edit2 className="w-3 h-3" />
                                                            Edit
                                                        </button>
                                                    )}
                                                    {invoice.url ? (
                                                        <button
                                                            onClick={() => handleDownload(
                                                                invoice.url,
                                                                invoice.invoiceNumber || invoice.id
                                                            )}
                                                            className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                                            title="Download Invoice"
                                                        >
                                                            <Download className="w-3 h-3" />
                                                            Download
                                                        </button>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">Not Available</span>
                                                    )}

                                                </div>
                                                {/* <div className="mt-1">
                                                    {status === 'standard' && (
                                                        <button
                                                            onClick={() => handleCreateTaxInvoice(invoice)}
                                                            className="bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 pl-5 rounded text-xs font-medium transition-colors flex items-center gap-1"
                                                            title="Convert to Tax Invoice"
                                                        >
                                                            <RefreshCw className="w-3 h-3" />
                                                            Create Tax Invoice
                                                        </button>
                                                    )}
                                                </div> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <TablePagination
                    component="div"
                    count={usersTotal}
                    page={usersPage}
                    onPageChange={handleChangePage}
                    rowsPerPage={usersLimit}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
                />
            </div>

            {/* Confirmation Modal for Tax Invoice Conversion */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">
                                Convert to Tax Invoice
                            </h3>
                            <button
                                onClick={handleCancelTaxConversion}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="mb-6">
                            <p className="text-gray-600">
                                Are you sure you want to convert this standard invoice to a tax invoice?
                                This will create a new tax invoice with the same details and allow you to make any necessary modifications.
                            </p>
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                <p className="text-sm text-yellow-800">
                                    <strong>Invoice:</strong> {selectedInvoiceForTaxConvert?.invoiceNumber || 'N/A'}
                                    <br />
                                    <strong>Company:</strong> {selectedInvoiceForTaxConvert?.companyName || 'N/A'}
                                </p>
                            </div>
                        </div>

                        <div className="flex justify-end gap-3">
                            <button
                                onClick={handleCancelTaxConversion}
                                className="px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleConfirmTaxConversion}
                                disabled={isConverting}
                                className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                            >
                                {isConverting ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                        Converting...
                                    </>
                                ) : (
                                    <>
                                        <RefreshCw className="w-4 h-4" />
                                        Convert to Tax Invoice
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* {isEditModalOpen && selectedInvoiceForEdit && (
                <InvoiceForm
                    isOpen={isEditModalOpen}
                    onClose={handleCloseEditModal}
                    selectedPlan={null}
                    userId={null}
                    editInvoiceData={selectedInvoiceForEdit}
                    isEditMode={true}
                />
            )} */}
        </>
    );
};

export default ExternalPaymemnt;